import { useMediaQuery, useTheme } from "@mui/material";
import { createContext, useContext, useEffect, useState } from "react";
import { useFindRecommendedPhases } from "../../hooks/useFindRecommendedPhases";
import { getDesktopSignupTutorial } from "./components/desktop.signup.tutorial";
import GlobalMenu from "./components/globalMenu";
import { getMobileSignupTutorial } from "./components/mobile.signup.tutorial";
import MobileGlobalMenu from "./components/MobileGlobalMenu";

export type Tutorial = {
    step: string;
    hint: React.ReactNode;
    title: string;
    action?: (...args: any[]) => void;
    error?: string;
    couldNotStepBack?: boolean;
    couldNotSkip?: boolean;
    fullContent?: boolean;
};

export type TutorialContextType = {
    tutorials: Tutorial[];
    activeStep: string | null;
    globalMenu: React.ReactNode;
    startTutorial: () => void;
    setActiveStep: (step: string) => void;
    nextStep: () => void;
    previousStep: () => void;
    stopTutorial: () => void;
};

export const TutorialContext = createContext<TutorialContextType>(null!);

export const TutorialProvider = ({
    children,
}: {
    children: React.ReactNode;
}) => {
    const recommendedPhases = useFindRecommendedPhases();

    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [tutorials, setTutorials] = useState<Tutorial[]>([]);

    useEffect(() => {
        setTutorials(
            isSmallScreen
                ? getMobileSignupTutorial(
                      recommendedPhases.map((p) => p.title).join("، ")
                  )
                : getDesktopSignupTutorial(
                      recommendedPhases.map((p) => p.title).join("، ")
                  )
        );
    }, [recommendedPhases, isSmallScreen]);

    const [activeStep, setActiveStep] = useState<string | null>(null);

    const startTutorial = () => {
        setActiveStep(tutorials[0].step);
    };

    const stopTutorial = () => {
        setActiveStep(null);
    };

    const nextStep = () => {
        const step = tutorials.indexOf(
            tutorials.find((s) => s.step === activeStep)
        );
        setActiveStep((p) => tutorials[step + 1]?.step ?? null);
    };
    const previousStep = () => {
        const step = tutorials.indexOf(
            tutorials.find((s) => s.step === activeStep)
        );
        setActiveStep((p) => tutorials[Math.max(step - 1, 0)].step);
    };

    return (
        <TutorialContext.Provider
            value={{
                tutorials,
                activeStep,
                setActiveStep,
                startTutorial,
                nextStep,
                previousStep,
                globalMenu: isSmallScreen ? (
                    <MobileGlobalMenu />
                ) : (
                    <GlobalMenu />
                ),
                stopTutorial,
            }}
        >
            {children}
        </TutorialContext.Provider>
    );
};

export const useTutorialContext = () => {
    return useContext(TutorialContext);
};
